/* global localStorage */
import animateScrollTo from 'animated-scroll-to'
import { connect } from 'unistore/preact'
import cx from 'classnames'

import s from './index.sass'
import gs from '../generic-sign-up.sass'
import Footer from '../../footer'
import { isMobile } from '../../../helpers/devices'
import ClubSection from '../club-section'
import GroupSection from './group-section'
import UpgradeSection from './upgrade-section'
import SummarySection from '../summary-section'
import BoundComponent from '../../../helpers/bound-component'
import MembershipSection from '../membership-section'
import Loader from '../../loader'
import prerendered from '../../../hoc/prerendered-content'
import {
  addCrumb,
  clearCrumbs
} from '../../header/actions'
import {
  updateInput,
  setInputAndAddOns,
  updateAddOns
} from './actions'
import {
  resetSignUp
} from '../actions'
import { getSuggested } from '../selectors'
import { getToken } from '../../../login'
import { route } from 'preact-router'
import { LS } from '../constants'
import { purchaseFunnelLoaded } from '../../../helpers/analytics'
import { pickProperties } from '../helpers'
import PricelistCampaign from '../pricelist-campaign'

export const defaultScrollOptions = {
  verticalOffset: -(isMobile() ? 80 + 43 : 99 + 61),
  speed: 750
}

const CenteredLoader = ({ class: _class, className, ...rest }) => (
  <div className={cx(gs.loaderContainer, className)} {...rest}>
    <Loader showImmediately />
  </div>
)

class Wizard extends BoundComponent {
  constructor (props) {
    super(props)
    const initParams = pickProperties(this.props.matches, ['club', 'group', 'upgrade', 'addOns'])
    const hasParams = Object.keys(initParams).length > 0
    const initialCollapsedState = !hasParams

    this.state = {
      initWithParams: hasParams && initParams,
      initLoading: hasParams,
      sections: {
        club: {
          isCollapsed: false // Never is collapsed
        },
        group: {
          isLoading: false,
          isCollapsed: initialCollapsedState
        },
        upgrade: {
          isLoading: false,
          isCollapsed: initialCollapsedState
        },
        summary: {
          isLoading: false,
          isCollapsed: initialCollapsedState
        },
        membership: {
          isCollapsed: true // Should always be collapsed.
        }
      }
    }

    this.bind = ['scrollTo', 'handleOnAddOn']

    this.references = {}
  }

  componentDidMount () {
    this.props.addCrumb('Bli medlem')
    this.props.addCrumb('Gym')

    if (this.state.initWithParams) {
      this.onInitWithParams().then(() => {
        this.setState({
          initLoading: false
        })
        setTimeout(() => this.scrollTo('summary'), 500)
      })
    } else {
      this.props.resetSignUp()
    }

    purchaseFunnelLoaded()
  }

  componentWillUnmount () {
    this.props.clearCrumbs()
  }

  async onInitWithParams () {
    const { addOns, ...inputs } = this.state.initWithParams
    const addOnsArray = addOns
      ? addOns.split(',').filter(v => v)
      : []

    await this.props.setInputAndAddOns(inputs, addOnsArray)
  }

  setRef (key) {
    return (element) => { this.references[key] = element }
  }

  addCrumb (input) {
    const { content: { guideInputs } } = this.props
    const crumb = guideInputs[input] && guideInputs[input].name
    if (crumb && !this.props.breadcrumbs.includes(crumb)) this.props.addCrumb(guideInputs[input].name)
  }

  toggleNextSection (current) {
    const { content: { guideInputs } } = this.props
    const nextIndex = guideInputs.order.indexOf(current) + 1
    let next = guideInputs.order[nextIndex]

    // Upgrade section is conditional, so we have to check if we're going to display it.
    const hasUpgrades = this.props.suggestion && this.props.suggestion.upgradeableTo.upgradeableMemberships.length > 0
    if (next === 'upgrade' && !hasUpgrades) {
      next = guideInputs.order[nextIndex + 1]
    }

    this.setState({
      sections: {
        ...this.state.sections,
        [current]: {
          ...this.state.sections[current],
          isLoading: false
        },
        [next]: {
          ...this.state.sections[next],
          isCollapsed: false
        }
      }
    })

    return next
  }

  buildQueryString () {
    const { signUpInput, signUpAddOns } = this.props

    const truthyInput = Object.keys(signUpInput).filter(key => signUpInput[key]).reduce((prev, curr) => ({ ...prev, [curr]: signUpInput[curr] }), {})
    const params = new URLSearchParams({ ...truthyInput, addOns: signUpAddOns.toString() })

    return `?${params.toString()}`
  }

  handleOnInput (value, currentInput) {
    this.setState({
      sections: {
        ...this.state.sections,
        [currentInput]: {
          ...this.state.sections[currentInput],
          isLoading: true
        }
      }
    }, () => {
      this.props.updateInput(currentInput, value).then(() => {
        window.history.replaceState(window.history.state, '', this.buildQueryString())
        const next = this.toggleNextSection(currentInput)
        this.addCrumb(next)
      })
    })
  }

  redirectToPayment () {
    localStorage.setItem(LS.shareUrl, window.location.href)
    route(`/bli-medlem/betalning`)
  }

  handleOnToNext () {
    if (getToken()) { // If logged in, go directly to payment
      this.redirectToPayment()
    } else {
      const nextInput = this.toggleNextSection('summary')
      this.addCrumb(nextInput)
    }
  }

  handleOnAddOn (value) {
    this.props.updateAddOns(value).then(() => {
      window.history.replaceState(window.history.state, '', this.buildQueryString())
    })
  }

  scrollTo (key) {
    const preactRef = this.references[key]
    if (preactRef && preactRef.base) animateScrollTo(preactRef.base.offsetTop, defaultScrollOptions)
  }

  render ({
    setSidebar,
    signUpSuggestion,
    signUpInput,
    signUpAddOns,
    suggestion,
    content
  }, {
    sections,
    initLoading
  }) {
    const { scrollTo, handleOnAddOn } = this.binds()

    const suggestionHasUpgrades = signUpSuggestion && signUpSuggestion.upgradeableTo.upgradeableMemberships.length > 0

    const srcData = content.guideInputs && {
      ...content.guideInputs,
      upgrade: {
        ...content.guideInputs.upgrade,
        inputs: signUpSuggestion && signUpSuggestion.upgradeableTo.upgradeableMemberships
      }
    }

    return !initLoading && srcData
      ? (
        <div>
          <div className={s.wizard}>
            <h1>{content.pageTitle}</h1>

            <div>
              {content.campaign && <div className={s.campaign}>
                <PricelistCampaign
                  title={content.campaign.title}
                  subTitle={content.campaign.subtitle}
                  text={content.campaign.description}
                  backgroundUrl={content.campaign.image && content.campaign.image.url}
                  videoUrl={content.campaign.video}
                  actionProps={{
                    label: content.campaign.buttonText,
                    href: content.campaign.url
                  }}
                />
              </div>}
            </div>

            <div>
              <ClubSection
                ref={this.setRef('club')}
                title={srcData.club.title}
                isCollapsed={sections.club.isCollapsed}
                onInput={(value) => this.handleOnInput(value, 'club')}
                preselect={this.props.matches.preselect}
                value={signUpInput.club}
                clubs={srcData.club.inputs}
              />
              {sections.club.isLoading && <CenteredLoader />}
            </div>

            <div>
              <GroupSection
                ref={this.setRef('group')}
                isCollapsed={sections.group.isCollapsed}
                onExpand={() => this.scrollTo('group')}
                onInput={(value) => this.handleOnInput(value, 'group')}
                title={srcData.group.title}
                description={srcData.group.description}
                additionalInfo={srcData.group.additionalInfo}
                inputs={srcData.group.inputs}
                value={signUpInput.group}
              />
              {sections.group.isLoading && <CenteredLoader />}
            </div>

            {(signUpSuggestion && suggestionHasUpgrades) && <div>
              <UpgradeSection
                ref={this.setRef('upgrade')}
                isCollapsed={sections.upgrade.isCollapsed}
                onExpand={() => this.scrollTo('upgrade')}
                onInput={(value) => this.handleOnInput(value, 'upgrade')}
                value={signUpInput.upgrade}
                title={signUpSuggestion.upgradeableTo.title}
                description={signUpSuggestion.upgradeableTo.description}
                inputs={signUpSuggestion.upgradeableTo.upgradeableMemberships}
                setSidebar={setSidebar}
              />
              {sections.upgrade.isLoading && <CenteredLoader />}
            </div>}

            <div>
              <SummarySection
                ref={this.setRef('summary')}
                isCollapsed={sections.summary.isCollapsed}
                onExpand={() => this.scrollTo('summary')}
                title={srcData.summary.title}
                srcData={srcData}
                inputs={signUpInput}
                onEdit={scrollTo}
                addOnsValue={signUpAddOns}
                addOnsCopy={content.addOnsCopy}
                handleOnAddOn={handleOnAddOn}
                suggestion={suggestion}
                includesTitle={srcData.summary.includesTitle}
                includes={srcData.summary.includes}
                onToPayment={() => this.handleOnToNext()}
              />
              {sections.summary.isLoading && <CenteredLoader />}
            </div>

            <MembershipSection
              ref={this.setRef('membership')}
              isCollapsed={sections.membership.isCollapsed}
              onExpand={() => this.scrollTo('membership')}
              title={srcData.membership.title}
              description={srcData.membership.description}
              redirect={() => this.redirectToPayment()}
            />
          </div>

          <Footer />
        </div>
      )
      : <CenteredLoader className={s.initLoader} />
  }
}

export const selectors = (state) => {
  const { breadcrumbs, signUpInput, signUpAddOns, signUpSuggestion } = state
  return {
    breadcrumbs,
    signUpInput,
    signUpAddOns,
    signUpSuggestion,
    suggestion: getSuggested(state)
  }
}

export default prerendered(connect(selectors, { addCrumb, clearCrumbs, updateInput, setInputAndAddOns, updateAddOns, resetSignUp })(Wizard))



// WEBPACK FOOTER //
// ./components/signup-flow/step-wizard/index.js